import React, { useMemo, useState } from "react";
import { UserPlus, X, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { jobsApi } from "../lib/api";
import { getDisqualifyReasons } from "../lib/techQualify";

const AVAILABILITY_DOT = {
  Available: "#10B981",
  "On Job": "#F59E0B",
  "En Route": "#6366F1",
  Lunch: "#94A3B8",
  Off: "#EF4444",
};

/**
 * Admin/Dispatcher-only "Assign Technician" / "Reassign" control for the Job
 * detail header (desktop Jobs.jsx + mobile MobileJobDetail.jsx) and the
 * Dispatch Board. Opens a multi-select list (checkbox + avatar + name +
 * live availability) confirmed with a single "Assign" button. Technicians
 * who don't match the job's trade/sub-category qualifications or aren't
 * scheduled on the job's date are shown greyed out but still tappable —
 * tapping one opens an override popup requiring the word "Override" to be
 * typed before they can be force-assigned.
 *
 * For non-admin/dispatcher viewers (canAssign=false) renders a read-only
 * summary instead, so technicians still see who's on the job.
 */
export default function AssignTechControl({ job, techs = [], canAssign, onJobUpdated, size = "md" }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [overrideTech, setOverrideTech] = useState(null); // { tech, reasons } | null
  const [overrideText, setOverrideText] = useState("");
  const [overrideBusy, setOverrideBusy] = useState(false);

  const assignments = job?.assignments || [];
  const assignedIds = useMemo(() => new Set(assignments.map((a) => a.techId)), [assignments]);
  const hasAssignees = assignments.length > 0;
  const chipCls = size === "sm" ? "text-[10.5px] px-2 py-0.5 h-[22px]" : "text-[12.5px] px-2.5 py-1 h-[26px]";

  if (!canAssign) {
    return (
      <div className="flex items-center gap-1.5 flex-wrap" data-testid={`assign-tech-readonly-${job.id}`}>
        {!hasAssignees ? (
          <span className={`inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-500 font-medium ${chipCls}`}>
            Unassigned
          </span>
        ) : (
          assignments.map((a) => (
            <span
              key={a.techId}
              className={`inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-800 border border-slate-200 font-medium ${chipCls}`}
            >
              {a.techName}
            </span>
          ))
        )}
      </div>
    );
  }

  const openSheet = () => {
    setSelected(assignments.map((a) => a.techId));
    setOpen(true);
  };

  const toggle = (techId) => {
    setSelected((prev) => (prev.includes(techId) ? prev.filter((x) => x !== techId) : [...prev, techId]));
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const toAdd = selected.filter((tid) => !assignedIds.has(tid));
      const toRemove = [...assignedIds].filter((tid) => !selected.includes(tid));
      let latest = job;
      for (const techId of toAdd) {
        latest = await jobsApi.addAssignment(job.id, { techId });
      }
      for (const techId of toRemove) {
        latest = await jobsApi.removeAssignment(job.id, techId);
      }
      onJobUpdated?.(latest);
      toast.success(selected.length ? "Technician(s) assigned" : "Job unassigned");
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update assignment");
    } finally {
      setBusy(false);
    }
  };

  const handleRowClick = (t, reasons) => {
    if (reasons.length === 0) {
      toggle(t.id);
    } else {
      setOverrideText("");
      setOverrideTech({ tech: t, reasons });
    }
  };

  const confirmOverride = async () => {
    if (!overrideTech) return;
    setOverrideBusy(true);
    try {
      const latest = await jobsApi.addAssignment(job.id, { techId: overrideTech.tech.id });
      onJobUpdated?.(latest);
      setSelected((prev) => (prev.includes(overrideTech.tech.id) ? prev : [...prev, overrideTech.tech.id]));
      toast.success(`${overrideTech.tech.name} assigned (override)`);
      setOverrideTech(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not assign technician");
    } finally {
      setOverrideBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={openSheet}
        data-testid={`assign-tech-trigger-${job.id}`}
        className={`inline-flex items-center gap-1.5 rounded-full font-bold transition-colors max-w-full truncate ${
          hasAssignees
            ? "bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
            : "bg-blue-600 text-white hover:bg-blue-700"
        } ${size === "sm" ? "text-[10.5px] px-2.5 py-1 h-[24px]" : "text-[13px] px-3.5 py-2 h-[36px]"}`}
      >
        <UserPlus className={size === "sm" ? "h-3 w-3 shrink-0" : "h-4 w-4 shrink-0"} />
        <span className="truncate">
          {hasAssignees ? `Reassign · ${assignments.map((a) => (a.techName || "").split(" ")[0]).join(", ")}` : "Assign Technician"}
        </span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[95] flex items-end sm:items-center justify-center bg-black/40"
          onClick={() => !busy && setOpen(false)}
          data-testid={`assign-tech-sheet-backdrop-${job.id}`}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-sm sm:rounded-2xl rounded-t-2xl bg-white shadow-2xl overflow-hidden flex flex-col max-h-[80vh]"
            data-testid={`assign-tech-sheet-${job.id}`}
          >
            <div className="px-4 py-3.5 border-b border-slate-100 flex items-center justify-between shrink-0">
              <div>
                <div className="text-[15px] font-bold text-slate-900">
                  {hasAssignees ? "Reassign Technician" : "Assign Technician"}
                </div>
                <div className="text-[11.5px] text-slate-500">Select one or more technicians</div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid={`assign-tech-sheet-close-${job.id}`}
                className="h-8 w-8 rounded-full hover:bg-slate-100 flex items-center justify-center shrink-0"
              >
                <X className="h-4 w-4 text-slate-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto py-1.5">
              {techs.length === 0 && (
                <div className="px-4 py-6 text-center text-[12.5px] text-slate-500">No technicians found.</div>
              )}
              {techs.map((t) => {
                const isSel = selected.includes(t.id);
                const reasons = getDisqualifyReasons(t, job);
                const disqualified = reasons.length > 0;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => handleRowClick(t, reasons)}
                    data-testid={disqualified ? `assign-tech-option-disqualified-${job.id}-${t.id}` : `assign-tech-option-${job.id}-${t.id}`}
                    title={disqualified ? reasons.join(" ") : undefined}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                      disqualified ? "opacity-45 hover:bg-slate-50" : "hover:bg-slate-50"
                    }`}
                  >
                    <div
                      className="h-9 w-9 rounded-full text-white text-[12px] font-bold flex items-center justify-center shrink-0"
                      style={{ background: disqualified ? "#94A3B8" : t.color || "#64748B" }}
                    >
                      {(t.avatar || (t.name || "?").slice(0, 2)).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-[13.5px] font-semibold truncate ${disqualified ? "text-slate-500" : "text-slate-900"}`}>
                        {t.name}
                      </div>
                      <div className="text-[11.5px] text-slate-500 flex items-center gap-1.5">
                        <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: AVAILABILITY_DOT[t.status] || "#94A3B8" }} />
                        {disqualified ? "Not qualified" : t.status || "Unknown"}
                      </div>
                    </div>
                    {disqualified ? (
                      <AlertTriangle className="h-4 w-4 text-slate-400 shrink-0" />
                    ) : (
                      <div
                        className={`h-5 w-5 rounded-md border-2 flex items-center justify-center shrink-0 ${
                          isSel ? "bg-blue-600 border-blue-600" : "border-slate-300"
                        }`}
                      >
                        {isSel && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="p-3 border-t border-slate-100 shrink-0" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
              <button
                type="button"
                onClick={confirm}
                disabled={busy}
                data-testid={`assign-tech-confirm-${job.id}`}
                className="w-full h-11 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-bold text-[14px] disabled:opacity-60 transition-colors"
              >
                {busy ? "Saving…" : `Assign${selected.length ? ` (${selected.length})` : ""}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {overrideTech && (
        <div
          className="fixed inset-0 z-[96] flex items-center justify-center bg-black/50 p-4"
          onClick={() => !overrideBusy && setOverrideTech(null)}
          data-testid={`assign-tech-override-backdrop-${job.id}`}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-[320px] rounded-2xl bg-white shadow-2xl p-4"
            data-testid={`assign-tech-override-popup-${job.id}-${overrideTech.tech.id}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4.5 w-4.5 text-amber-500 shrink-0" />
              <div className="text-[14px] font-bold text-slate-900">Can't assign {overrideTech.tech.name}</div>
            </div>
            <ul className="text-[12.5px] text-slate-600 space-y-1 mb-3 pl-1">
              {overrideTech.reasons.map((r) => (
                <li key={r}>• {r}</li>
              ))}
            </ul>
            <input
              type="text"
              value={overrideText}
              onChange={(e) => setOverrideText(e.target.value)}
              placeholder="Type 'Override' to still assign this technician"
              data-testid={`assign-tech-override-input-${job.id}-${overrideTech.tech.id}`}
              className="w-full h-10 px-3 rounded-lg border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 mb-3"
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setOverrideTech(null)}
                data-testid={`assign-tech-override-cancel-${job.id}-${overrideTech.tech.id}`}
                className="flex-1 h-9 rounded-full border border-slate-200 text-slate-600 text-[13px] font-semibold hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmOverride}
                disabled={overrideText.trim().toLowerCase() !== "override" || overrideBusy}
                data-testid={`assign-tech-override-save-${job.id}-${overrideTech.tech.id}`}
                className="flex-1 h-9 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-bold disabled:opacity-40 transition-colors"
              >
                {overrideBusy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
